import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { productsService, productLinesService, warehousesService } from '../../services/productionService';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import useLookup from '../../utils/useLookup';
import { STATUS_OPTIONS } from '../../constants/listOptions';
import ConfirmDialog from '../../components/ConfirmDialog';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { formatBRL, formatQuantity } from '../../lib/utils';

function ProductsList() {
    usePageTitle('Produtos');
    const navigate = useNavigate();
    const { toast, toastApiError } = useToast();
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const warehouses = useLookup(() => warehousesService.list({ per_page: 1000 }));

    const productLines = useLookup(() => productLinesService.list({ per_page: 1000 }));

    const list = useTableList({
        storageKey: 'products',
        fetcher: (params) => productsService.list(params),
    });

    const handleNewProduct = () => {
        navigate('/production/products/new');
    };

    const handleEditProduct = (product) => {
        navigate(`/production/products/${product.id}/edit`);
    };

    const handleDeleteClick = (product) => {
        setDeleteDialog({ open: true, id: product.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await productsService.delete(deleteDialog.id);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({
                title: 'Produto excluído',
                description: 'O produto foi excluído com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error deleting product:', error);
            toastApiError(error, { title: 'Erro ao excluir', fallback: 'Erro ao excluir produto' });
        }
    };


    const getStatusBadge = (status) => {
        const statusMap = {
            active: { label: 'Ativo', variant: 'success' },
            inactive: { label: 'Inativo', variant: 'secondary' },
        };
        const config = statusMap[status] || { label: status, variant: 'default' };
        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const columns = [
        {
            key: 'code',
            label: 'Código',
            className: 'font-medium',
        },
        {
            key: 'description',
            label: 'Descrição',
        },
        {
            key: 'product_line',
            label: 'Linha',
            render: (value, row) => row.product_line?.name || '-',
        },
        {
            key: 'unit',
            label: 'Unidade',
        },
        {
            key: 'stock_quantity',
            label: 'Estoque',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => formatQuantity(value),
        },
        {
            key: 'unit_cost',
            label: 'Custo Unitário',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => formatBRL(value),
        },
        {
            key: 'sale_price',
            label: 'Preço de Venda',
            align: 'right',
            className: 'text-right tabular-nums',
            render: (value) => (value != null ? formatBRL(value) : '-'),
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => getStatusBadge(value),
        },
    ];

    const filters = [
        { key: 'code', label: 'Código', type: 'text' },
        { key: 'description', label: 'Descrição', type: 'text' },
        { key: 'unit', label: 'Unidade', type: 'text' },
        { key: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
        { key: 'product_line_id', label: 'Linha', type: 'select', options: productLines.map((line) => ({ value: String(line.id), label: line.name })) },
        { key: 'default_warehouse_id', label: 'Depósito padrão', type: 'select', options: warehouses.map((w) => ({ value: String(w.id), label: w.name })) },
        { key: 'price_min', label: 'Preço de venda mínimo', type: 'number' },
        { key: 'price_max', label: 'Preço de venda máximo', type: 'number' },
    ];

    return (
        <AppLayout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title="Produtos"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção', path: '/production/products' },
                    ]}
                    actions={[
                        {
                            label: 'Novo Produto',
                            icon: 'new',
                            as: Link,
                            to: '/production/products/new',
                        },
                    ]}
                />

                {/* Table */}
                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por código ou descrição..."
                    columns={columns}
                    editTo={(row) => `/production/products/${row.id}/edit`}
                    onDelete={handleDeleteClick}
                    emptyMessage="Nenhum produto cadastrado"
                />
            </div>

            {/* Confirm Dialog */}
            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir Produto"
                message="Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />
        </AppLayout>
    );
}

export default ProductsList;

